import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { setCustomerToken } from "../api";
import { usePageTitle } from "../components/common";
import { PHOTO_PRIVACY } from "../content";

export default function Intake() {
  usePageTitle("Mes informations");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", delivery_method: "whatsapp",
    phone: "", question_or_intention: "",
  });
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/order/by-session/${sessionId}`).then((r) => {
      setOrder(r.data);
      setForm((f) => ({ ...f, email: r.data.customer_email || "" }));
      if (r.data.intake_done) {
        if (r.data.booking_required && !r.data.booking_done) navigate(`/reservation?session_id=${sessionId}`);
      }
    }).catch(() => {});
  }, [sessionId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!ok.includes(f.type)) { toast.error("Format non accepté (JPG, JPEG, PNG, WEBP)."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("La photo dépasse 10 Mo."); return; }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const isEnergy = order?.category === "energetique";

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) { toast.error("Merci d'ajouter une photo récente."); return; }
    if (form.delivery_method === "whatsapp" && !form.phone.trim()) {
      toast.error("Le numéro WhatsApp est requis."); return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("photo", photo);
      const { data } = await api.post("/intake", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.access_token) setCustomerToken(data.access_token);
      toast.success("Tes informations sont bien reçues ✨");
      if (data.booking_required) navigate(`/reservation?session_id=${sessionId}`);
      else navigate(`/confirmation?session_id=${sessionId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  if (!sessionId) return <div className="container-x py-24 text-center text-[#7A5D58]">Session introuvable.</div>;

  return (
    <div className="container-x py-10 sm:py-16 fade-up max-w-2xl">
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">Tes informations</h1>
      <p className="mt-2 text-sm text-[#7A5D58]">
        {order ? order.service_name : ""} — ces éléments me permettent de me consacrer pleinement à ta séance.
      </p>
      <div className="gold-divider my-6 w-28" />

      <form onSubmit={submit} className="space-y-5" data-testid="intake-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Prénom</label>
            <input required className="field-input" value={form.first_name} onChange={set("first_name")} data-testid="intake-first" />
          </div>
          <div>
            <label className="field-label">Nom</label>
            <input required className="field-input" value={form.last_name} onChange={set("last_name")} data-testid="intake-last" />
          </div>
        </div>
        <div>
          <label className="field-label">Email</label>
          <input required type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="intake-email" />
        </div>

        {/* Delivery method */}
        <div>
          <label className="field-label">Comment souhaites-tu recevoir ton compte rendu ?</label>
          <div className="flex gap-3">
            {[{ v: "whatsapp", l: "WhatsApp" }, { v: "email", l: "Email" }].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, delivery_method: o.v })}
                data-testid={`delivery-${o.v}`}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                  form.delivery_method === o.v ? "border-[#5A3A4B] bg-[#5A3A4B] text-[#FCFAF7]" : "border-[#7A5D58]/25 text-[#5A3A4B]"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
        {form.delivery_method === "whatsapp" && (
          <div>
            <label className="field-label">Numéro WhatsApp</label>
            <input required className="field-input" value={form.phone} onChange={set("phone")} placeholder="+33…" data-testid="intake-phone" />
          </div>
        )}

        {/* Intention — énergétique uniquement (la guidance est déjà renseignée sur la fiche) */}
        {isEnergy && (
          <div>
            <label className="field-label">Ton intention pour cette séance — facultatif</label>
            <textarea
              rows={4}
              className="field-input resize-none"
              value={form.question_or_intention}
              onChange={set("question_or_intention")}
              data-testid="intake-question"
            />
          </div>
        )}

        {/* Photo */}
        <div>
          <label className="field-label">Ta photo récente</label>
          <p className="text-xs text-[#7A5D58] mb-2">
            Ta photo doit être récente, montrer clairement ton visage et te représenter seule.
          </p>
          <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#7A5D58]/30 bg-[#FCFAF7] p-6 cursor-pointer hover:border-[#5A3A4B]/50 transition" data-testid="intake-photo-drop">
            {preview ? (
              <img src={preview} alt="Aperçu" className="max-h-48 rounded-lg object-contain" />
            ) : (
              <>
                <Upload className="text-[#C9A56A]" size={26} />
                <span className="text-sm text-[#7A5D58]">Choisir une photo (JPG, PNG, WEBP · max 10 Mo)</span>
              </>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} className="hidden" data-testid="intake-photo-input" />
          </label>
          <div className="mt-3 flex gap-2 text-xs text-[#7A5D58] bg-[#C9A56A]/10 rounded-xl p-3">
            <ImageIcon size={15} className="text-[#C9A56A] shrink-0 mt-0.5" />
            <span className="whitespace-pre-line leading-relaxed">{PHOTO_PRIVACY}</span>
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="intake-submit">
          {loading ? "Envoi…" : "Transmettre mes informations"}
        </button>
      </form>
    </div>
  );
}
